// pages/admin/AdminClassifications.js
// ניהול סיווגים — CRUD
//
// API:
//   GET /classifications → רשימה
//   POST /classifications { name, description } → יצירה
//   PUT /classifications/{id} { name, description } → עדכון
//   DELETE /classifications/{id} → מחיקה (hard delete)
//
// UI: DataTable + Add button + Edit dialog + Delete confirm
// מבנה כמעט זהה ל-AdminUnits

import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LabelIcon from '@mui/icons-material/LabelOutlined';
import { useTranslation } from 'react-i18next';

import { DataTable, ConfirmDialog } from '../../components/shared';
import { useApi, useApiMutation } from '../../hooks/useApi';

// =============================================================================
// AdminClassifications Page
// =============================================================================
export default function AdminClassifications() {
  const { t } = useTranslation();

  // API — fetch classifications list
  const { data: classifications, loading, error, refetch } = useApi('/classifications', {
    defaultData: [],
  });

  // Mutation hook
  const { mutate, loading: mutating } = useApiMutation();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = add, object = edit
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formError, setFormError] = useState('');

  // Delete confirm state
  const [deleteItem, setDeleteItem] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ===================================================================
  // Dialog handlers
  // ===================================================================
  const handleOpenAdd = useCallback(() => {
    setEditingItem(null);
    setFormName('');
    setFormDescription('');
    setFormError('');
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((item) => {
    setEditingItem(item);
    setFormName(item.name || '');
    setFormDescription(item.description || '');
    setFormError('');
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (mutating) return;
    setDialogOpen(false);
    setEditingItem(null);
  }, [mutating]);

  // ===================================================================
  // Save (create or update)
  // ===================================================================
  const handleSave = useCallback(async () => {
    if (mutating) return;
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError(t('common.required'));
      return;
    }

    setFormError('');
    const body = {
      name: trimmedName,
      description: formDescription.trim() || null,
    };

    let result;
    if (editingItem) {
      result = await mutate('put', `/classifications/${editingItem.id}`, body);
    } else {
      result = await mutate('post', '/classifications', body);
    }

    if (result.success) {
      setDialogOpen(false);
      setEditingItem(null);
      refetch();
      setSnackbar({
        open: true,
        message: result.message || t('common.success'),
        severity: 'success',
      });
    } else {
      setFormError(result.error || t('common.error'));
    }
  }, [formName, formDescription, editingItem, mutate, mutating, refetch, t]);

  // ===================================================================
  // Delete
  // ===================================================================
  const handleDelete = useCallback(async () => {
    if (!deleteItem || mutating) return;

    const result = await mutate('delete', `/classifications/${deleteItem.id}`);

    if (result.success) {
      setDeleteItem(null);
      refetch();
      setSnackbar({
        open: true,
        message: result.message || t('common.success'),
        severity: 'success',
      });
    } else {
      setDeleteItem(null);
      setSnackbar({
        open: true,
        message: result.error || t('common.error'),
        severity: 'error',
      });
    }
  }, [deleteItem, mutating, mutate, refetch, t]);

  // ===================================================================
  // Table config
  // ===================================================================
  const columns = useMemo(() => [
    {
      key: 'name',
      label: t('classifications.classificationName'),
      sortable: true,
    },
    {
      key: 'description',
      label: t('common.description'),
      sortable: false,
      render: (val) => val || '—',
    },
    {
      key: 'active',
      label: t('common.status'),
      sortable: true,
      width: 100,
      render: (val) => (
        <Chip
          label={val ? t('common.active') : t('common.inactive')}
          color={val ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
  ], [t]);

  const actions = useMemo(() => [
    {
      key: 'edit',
      icon: <EditIcon fontSize="small" />,
      label: t('common.edit'),
      onClick: handleOpenEdit,
    },
    {
      key: 'delete',
      icon: <DeleteIcon fontSize="small" />,
      label: t('common.delete'),
      color: 'error',
      onClick: (item) => setDeleteItem(item),
    },
  ], [t, handleOpenEdit]);

  const toolbar = useMemo(() => (
    <Button
      data-testid="classifications__button--add"
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleOpenAdd}
      size="small"
    >
      {t('classifications.addClassification')}
    </Button>
  ), [t, handleOpenAdd]);

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="classifications__page" sx={{ pb: 4 }}>
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <LabelIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          {t('classifications.title')}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Table */}
      <DataTable
        testId="classifications__table"
        columns={columns}
        rows={classifications || []}
        actions={actions}
        loading={loading}
        searchable
        toolbar={toolbar}
        defaultSortKey="name"
        emptyStateProps={{
          title: t('classifications.noClassifications'),
          message: t('classifications.noClassificationsMessage'),
          actionLabel: t('classifications.addClassification'),
          onAction: handleOpenAdd,
        }}
      />

      {/* ============================================================= */}
      {/* Add/Edit Dialog                                                */}
      {/* ============================================================= */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        data-testid="classifications__dialog--edit"
      >
        {mutating && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingItem ? t('classifications.editClassification') : t('classifications.addClassification')}
          <IconButton aria-label={t('common.close')} size="small" onClick={handleCloseDialog} disabled={mutating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              data-testid="classifications__dialog--edit__input--name"
              label={t('classifications.classificationName')}
              value={formName}
              onChange={(e) => {
                setFormName(e.target.value);
                setFormError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              fullWidth
              autoFocus
              required
              disabled={mutating}
              error={!!formError}
              helperText={formError}
            />

            <TextField
              data-testid="classifications__dialog--edit__input--description"
              label={t('common.description')}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={mutating}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button data-testid="classifications__dialog--edit__button--cancel" onClick={handleCloseDialog} disabled={mutating}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="classifications__dialog--edit__button--save"
            variant="contained"
            onClick={handleSave}
            disabled={mutating || !formName.trim()}
          >
            {mutating ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Delete Confirm Dialog                                          */}
      {/* ============================================================= */}
      <ConfirmDialog
        open={!!deleteItem}
        title={t('classifications.deleteClassification')}
        message={t('classifications.deleteConfirm', { name: deleteItem?.name })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteItem(null)}
        confirmLabel={t('common.delete')}
        destructive
        loading={mutating}
      />

      {/* ============================================================= */}
      {/* Snackbar                                                       */}
      {/* ============================================================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
